import React, { useState, type ReactNode, type HTMLAttributes } from "react";
import Box from "@mui/material/Box";
import ToolTipComponent from "../tooltip";
import type { TranslatedString } from "@i18n";
import type { SxProps, Theme } from "@mui/material/styles";
import type { TooltipPosition } from "../../interface";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode;
  className?: string;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  tooltipContent?: TranslatedString | React.ReactElement;
  alwaysShowTooltip?: boolean;
  sx?: SxProps<Theme>;
  placement?: TooltipPosition;
  grid?: boolean;
  noWrap?: boolean;
  hideToolTip?: boolean;
}

const sxMapper = {
  grid: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
  },
  normal: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  },
  noWrap: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "normal",
  },
};

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style = {},
  sx,
  placement,
  grid = false,
  noWrap = false,
  hideToolTip = false,
  ...rest
}) => {
  const [tooltipTitle, setTooltipTitle] = useState<TranslatedString | React.ReactElement | null>(null);
  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    // 1px tolerance, the same one the delegated grid tooltip uses (gridCellTooltip.ts) and for the
    // same reason: `.slick-cell` sets `line-height: 1.3rem` while index.css clamps the root font
    // size (`clamp(0.75rem, 1vw, 1rem)`), so a line box lands on a FRACTIONAL pixel height. The
    // browser rounds `clientHeight` down and `scrollHeight` up, leaving a permanent 1px gap on text
    // that is not clipped at all - and a bare `>` reads that as overflow, which is why a tooltip
    // appeared on hover of EVERY cell regardless of truncation.
    const hasOverflow = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;

    if (
      alwaysShowTooltip ||
      (!hideToolTip && hasOverflow && ((sx as { whiteSpace?: string } | undefined)?.whiteSpace != "normal" || noWrap))
    ) {
      setTooltipTitle(tooltipContent || (String(children) as TranslatedString) || null);
    }
  };

  const handleMouseLeave = () => {
    setTooltipTitle(null);
  };

  return (
    <ToolTipComponent content={tooltipTitle} position={placement ?? "top"}>
      <Box
        component="div"
        className={className}
        sx={
          {
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
            fontFamily: "inherit",
            ...sxMapper[grid ? "grid" : noWrap ? "noWrap" : "normal"],
            ...sx,
            ...style,
          } as SxProps<Theme>
        }
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
